import axios from "axios"
import qs from 'qs'
import redux, { Dispatch, Reducer } from 'redux'
import { API_KEY, API_PATH } from '../config'
import { GetState } from './index'

export interface ProfileSettings {
  birthday: string
  anrede: string
  firstname: string
  middlename: string
  lastname: string
  username: string
  title: string
  post_code: string
  city: string
  street: string
  profession: string
  country: string
  mail: string
  tel: string
}

export interface ProfileCountry {
  DisplayText: string
  Value: string
}

/*
contract_id: 4690
contract_type: "KB"
status: "abgeschlossen"
end_date: "2020-09-01"
array_position: 0
ins_company: "Donau Allgemeine Versicherungs-Aktiengesellschaft"
isShort: 0
amount_period: "32,45"
additionalDoc: "0"
cccActive: false
pay_period: "monatlich"
car_seats: 0
car_max_weight: 0
car_power: 0
car_ps: 0
is_visible: 0
car_thirdparty_sum: 0
car_thirdparty_fee: 0
car_kasko_sum: 0
car_kasko_fee: 0
car_laywer_sum: 0
car_laywer_fee: 0
car_accident_sum: 0
car_accident_fee: 0
mitversichert: "Nein"
amountWithCarFee: 32.45
proagent_id: 0
polizzennummer: ""
vertrags_beginn: "2019-08-02"
praemie_zahl_ust: 0
 */
export interface ProfileContract {
  contract_id: number
  proagent_id: number
  contract_type: string
  status: string
  end_date: string
  ins_company: string
  amount_period: string
  pay_period: string
  polizzennummer?: string
  vertrags_beginn: string
}

export interface AuthState {
  authenticated?: boolean
  settings?: ProfileSettings
  countries: ProfileCountry[]
  contracts: ProfileContract[]
}

type ActionTypes = 'AUTH_INIT' | 'AUTH_LOGOUT' | 'AUTH_UPDATE_SETTINGS';
type Action = redux.Action<ActionTypes> & redux.AnyAction

export const actionTypes: {[key: string]: ActionTypes} = {
  INIT: 'AUTH_INIT',
  LOGOUT: 'AUTH_LOGOUT',
  UPDATE_SETTINGS: 'AUTH_UPDATE_SETTINGS',
};

const initialState: AuthState = {
  countries: [],
  contracts: [],
}

export const auth: Reducer<AuthState, Action> = (state = initialState, action: Action): AuthState => {
  switch (action.type) {
    case actionTypes.INIT:
      return Object.assign({}, state, {
        authenticated: action.authenticated,
        settings: action.settings,
        contracts: action.contracts,
        countries: action.countries || state.countries,
      })

    case actionTypes.LOGOUT:
      return Object.assign({}, state, {
        authenticated: action.authenticated,
        settings: null
      })

    case actionTypes.UPDATE_SETTINGS:
      return Object.assign({}, state, {
        settings: {
          ...state.settings,
          ...action.settings
        }
      })


  }

  return state
}

let prevUpdate: any

export type updateSettingsType = (partial: Partial<ProfileSettings>) => any
export const updateSettings = (partial: Partial<ProfileSettings>) => async (dispatch: Dispatch<Action>, getState: GetState) => {
  if (prevUpdate) {
    prevUpdate()
  }

  const {auth} = getState()
  const old = auth.settings as ProfileSettings

  // check if should update
  const changed = Object.keys(partial).reduce<boolean>((previousValue, key) => {
    if ((partial as any)[key] !== (auth.settings as any)[key]) {
      return true
    }

    return previousValue
  }, false)

  if (!changed) {
    return false
  }

  const c = await new Promise(resolve => {
    const timer = setTimeout(() => {
      resolve(true)
      prevUpdate = null
    }, 1000)

    prevUpdate = () => {
      clearTimeout(timer)
      resolve(false)
    }
  })

  if (!c) {
    console.log('skipped update')
    return false
  }

  const update = async (data: any) => {
    await axios.post(
      `${API_PATH}/onlineordner/ChangeSettings`,
      qs.stringify(data),
      {
        withCredentials: true,
        headers: {
          'x-api-key': API_KEY,
          'content-type': 'application/x-www-form-urlencoded;charset=utf-8',
        },
      },
    )
  }

  if (
    partial.anrede !== old.anrede
    || partial.title !== old.title
    || partial.firstname !== old.firstname
    || partial.middlename !== old.middlename
    || partial.lastname !== old.lastname
  ) {
    await update({
      type: 'name',
      newAnrede: partial.anrede,
      newTitle: partial.title,
      newFirstname: partial.firstname,
      newMiddlename: partial.middlename,
      newLastname: partial.lastname,
    })
  }

  if (old.profession !== partial.profession) {
    await update({
      type: 'occupation',
      newOccupation: partial.profession,
    })
  }

  if (old.tel !== partial.tel) {
    await update({
      type: 'tel',
      newTel: partial.tel,
    })
  }

  if (
    old.street !== partial.street
    || old.post_code !== partial.post_code
    || old.city !== partial.city
    || old.country !== partial.country
  ) {
    await update({
      type: 'adr',
      newStreet: partial.street,
      newZIP: partial.post_code,
      newCity: partial.city,
      newCountry: partial.country,
    })
  }

  await dispatch({
    type: actionTypes.UPDATE_SETTINGS,
    settings: partial,
  })

  return true
}

export type logoutType = () => any
export const logout = () => async (dispatch: Dispatch<Action>) => {
  await axios.get(
    `${API_PATH}/onlineordner/LogOut`,
    {
      withCredentials: true,
      headers: {
        'x-api-key': API_KEY,
      }
    }
  )

  localStorage.removeItem('cookie')

  await authInitialize()(dispatch)
}

export type loginType = (username: string, password: string) => any
export const login = (username: string, password: string) => async (dispatch: Dispatch<Action>) => {
  try {
    const result = await axios.post(
      `${API_PATH}/onlineordner/LoginCheck`,
      qs.stringify({
        loginName: username,
        password,
        login: 'Anmelden'
      }),
      {
        withCredentials: true,
        headers: {
          'x-api-key': API_KEY,
          'content-type': 'application/x-www-form-urlencoded;charset=utf-8',
        },
      },
    )

    localStorage.setItem('cookie', result.data.cookie)
  } catch (e) {
    console.error('failed to login', e)

    return false
  }

  return await authInitialize()(dispatch)
}

export const authInitialize = () => async (dispatch: Dispatch<Action>) => {
  console.log('init auth')

  try {
    const cookie = localStorage.getItem('cookie')
    if (cookie) {
      console.log('set x-cookie from localstorage')
      axios.defaults.headers['x-cookie'] = cookie
    }

    const [
      settingsResult,
      countriesResult,
      contractsResult,
    ] = await Promise.all([
      ...[
        '/GetCustomerSettings?idString=general',
        '/GetNationsAsOptions',
      ].map(url => axios.get(
        `${API_PATH}/onlineordner${url}`,
        {
          withCredentials: true,
          headers: {
            'x-api-key': API_KEY,
          },
        }
      )),
      axios.post(
        `${API_PATH}/onlineordner/GetCustomerContracts?action=list`,
        null,
        {
          withCredentials: true,
          headers: {
            'x-api-key': API_KEY,
          },
        }
      )
    ])

    await dispatch({
      type: actionTypes.INIT,
      settings: settingsResult.data[0],
      countries: countriesResult.data,
      contracts: contractsResult.data.Records,
      authenticated: true
    })

    return true
  } catch (e) {
    console.error('failed to load customer settings')
    await dispatch({
      type: actionTypes.INIT,
      authenticated: false,
      settings: null,
    })

    return false
  }
}
